import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Typography,
  Box,
  Alert,
  IconButton,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Save as SaveIcon,
  AccountBalance,
} from '@mui/icons-material';
import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import CpdTextField from '@/components/ui/CpdTextField';
import CpdLabeledValue from '@/components/ui/CpdLabeledValue';
import CpdActionButton from '@/components/ui/CpdActionButton';
import { useGetClientBankAccount } from '@/api/hooks/client-bank-accounts/useGetClientBankAccount';
import { ClientBankAccountsApi } from '@/api/clients/client-bank-accounts';
import { formatTimestamp } from '@/utils/date/date';
import { useConfirmation } from '@/context/ConfirmationContext';
import { parseValidationErrors } from '@/utils/validationErrors';

export default function ClientBankAccountDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: organizationId, accountId } = useParams();
  const { requestConfirmation } = useConfirmation();
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [checkTemplate, setCheckTemplate] = useState('');
  const [templateError, setTemplateError] = useState('');
  const [error, setError] = useState('');

  const {
    data: bankAccount,
    loading,
    error: fetchError,
    refetch,
  } = useGetClientBankAccount(organizationId, accountId);

  useEffect(() => {
    if (bankAccount) {
      setDisplayName(bankAccount.display_name);
      setCheckTemplate(bankAccount.check_template || '');
    }
  }, [bankAccount]);

  const handleSave = async () => {
    if (bankAccount.deactivated_at) {
      return;
    }
    if (!displayName) {
      setError(t('clientBankAccounts.validation.displayNameRequired'));
      return;
    }

    const payload = { display_name: displayName };
    if (bankAccount.provider === 'self_hosted') {
      const normalizedTemplate = checkTemplate.trim();
      if (Array.from(normalizedTemplate).length > 35) {
        setTemplateError(
          t('clientBankAccounts.validation.checkTemplateLength'),
        );
        return;
      }
      payload.check_template = normalizedTemplate || null;
    }

    setSaving(true);
    try {
      await ClientBankAccountsApi.updateClientBankAccount(
        organizationId,
        accountId,
        payload,
      );
      toast.success(t('clientBankAccounts.updateSuccess'));
      refetch();
    } catch (err) {
      const { fieldErrors } = parseValidationErrors(err.response?.data);
      setTemplateError(fieldErrors.check_template || '');
      console.error('Error updating bank account:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(
      `/admin/updateOrganization/${organizationId}#client-bank-accounts`,
    );
  };

  const handleDeactivate = () => {
    const confirmMessage = t('clientBankAccounts.confirmDeactivate', {
      name: bankAccount.display_name,
    });

    requestConfirmation(confirmMessage, async () => {
      try {
        await ClientBankAccountsApi.deleteClientBankAccount(
          organizationId,
          accountId,
        );
        toast.success(t('clientBankAccounts.deactivateSuccess'));
        refetch();
      } catch (err) {
        console.error('Error deactivating bank account:', err);
      }
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>{fetchError}</Alert>
      </Box>
    );
  }

  if (!bankAccount) {
    return null;
  }

  const isDeactivated = !!bankAccount.deactivated_at;
  const isSelfHosted = bankAccount.provider === 'self_hosted';
  let editLimitation = t('clientBankAccounts.editLimitation');
  if (isSelfHosted) {
    editLimitation = t('clientBankAccounts.wellsFargoEditLimitation');
  }

  return (
    <PageLayout
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBack sx={{ color: 'white' }} />
            </IconButton>
            <AccountBalance sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              {t('clientBankAccounts.detailsTitle')}
            </Typography>
          </Box>
        </CardHeader>
      }>
      {isDeactivated && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          {t('clientBankAccounts.deactivatedMessage', {
            date: formatTimestamp(bankAccount.deactivated_at),
          })}
        </Alert>
      )}

      <Alert severity='info' sx={{ mb: 3 }}>
        {editLimitation}
      </Alert>

      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <CpdLabeledValue
            label={t('clientBankAccounts.form.businessPayeeName')}
            value={bankAccount.business_payee_name}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CpdLabeledValue
            label={t('clientBankAccounts.form.accountNumber')}
            value={`****${bankAccount.account_last_four}`}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CpdLabeledValue
            label={t('clientBankAccounts.form.routingNumber')}
            value={bankAccount.routing_number}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CpdLabeledValue
            label={t('clientBankAccounts.form.bankName')}
            value={bankAccount.bank_name || '-'}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <CpdLabeledValue
            label={t('clientBankAccounts.form.provider')}
            value={bankAccount.provider}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <CpdLabeledValue
              label={t('clientBankAccounts.form.createdAt')}
              value={formatTimestamp(bankAccount.created_at)}
            />
            {bankAccount.created_by_email && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}>
                {bankAccount.created_by_email}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <CpdLabeledValue
              label={t('clientBankAccounts.form.updatedAt')}
              value={formatTimestamp(bankAccount.updated_at)}
              labelVariant='body2'
            />
            {bankAccount.updated_by_email && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}>
                {bankAccount.updated_by_email}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <CpdTextField
            sx={{ mt: 2 }}
            label={t('clientBankAccounts.form.displayName')}
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setError('');
            }}
            disabled={isDeactivated}
            error={!!error}
            helperText={error}
          />
        </Grid>
        {isSelfHosted && (
          <Grid item xs={12}>
            <CpdTextField
              label={t('clientBankAccounts.form.checkTemplate')}
              value={checkTemplate}
              onChange={(e) => {
                setCheckTemplate(e.target.value);
                setTemplateError('');
              }}
              disabled={isDeactivated}
              error={!!templateError}
              helperText={templateError}
              autoComplete='off'
            />
          </Grid>
        )}
      </Grid>

      {!isDeactivated && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: 3,
          }}>
          <Button variant='outlined' color='error' onClick={handleDeactivate}>
            {t('clientBankAccounts.deactivate')}
          </Button>

          <CpdActionButton
            onClick={handleSave}
            loading={saving}
            startIcon={<SaveIcon sx={{ color: 'white' }} />}
            disabled={saving || !displayName}>
            {saving ? 'Saving...' : 'Save Changes'}
          </CpdActionButton>
        </Box>
      )}
    </PageLayout>
  );
}
